import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress,
    Alert,
} from "@mui/material";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";

// Per-student prep progress report. Exports as a direct PDF download (not saved to the
// course's Class Materials — those are for textbooks).
const ExportPrepReportModal = ({ open, onClose, programUuid, programName, student, periodSel = {} }) => {
    const period = periodSel.period || "all";
    const [title, setTitle] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const studentName = (student?.name || "").trim();

    useEffect(() => {
        if (open) {
            const sn = (studentName || "student").replace(/\s+/g, "_");
            const pn = (programName || "prep").replace(/\s+/g, "_");
            setTitle(`${pn}_${sn}_${period || "all"}.pdf`);
            setError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleExport = async () => {
        if (!title.trim()) return setError("Please enter a file name");
        const fileName = title.trim().endsWith(".pdf") ? title.trim() : `${title.trim()}.pdf`;
        setLoading(true);
        setError("");
        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}/students/${student.student_id}/export-pdf`,
                { title: fileName, period, from: periodSel.from, to: periodSel.to },
                { headers: { Authorization: getAuthHeader() }, responseType: "blob" }
            );
            // Stream the returned PDF straight to a browser download.
            const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
            onClose();
        } catch (err) {
            console.error("Export prep report error:", err);
            setError("Failed to export report");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Export Progress Report (PDF)</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                    fullWidth
                    sx={{ mt: 1 }}
                    label="File name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={loading}
                    helperText="Auto-generated; editable. Downloads to your computer."
                />
                <Box sx={{ mt: 1, color: "text.secondary", fontSize: 13 }}>Period: {period || "all"}</Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleExport}
                    disabled={loading || !title.trim()}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? "Exporting..." : "Export"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportPrepReportModal;
